import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronUp } from 'lucide-react';
import { clsx } from 'clsx';
import { useProperty } from '@/app/providers';
import { usePricingWorkflow } from '../hooks/usePricingWorkflow';
import { PricingWorkflowStatus } from './PricingWorkflowStatus';

const COLLAPSE_AFTER_MS = 30000;

const FloatingWorkflowStatus: React.FC = () => {
  const { selectedProperty } = useProperty();
  const propertyId = selectedProperty?.id;

  if (!propertyId) return null;

  return <FloatingWorkflowStatusInner propertyId={propertyId} />;
};

const FloatingWorkflowStatusInner: React.FC<{ propertyId: string }> = ({
  propertyId,
}) => {
  const { status, isExecuting, lastResult, error } = usePricingWorkflow({
    propertyId,
  });

  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const collapseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prevStatusRef = useRef(status);

  const isActive = !['idle', 'completed', 'failed'].includes(status);
  const isTerminal = status === 'completed' || status === 'failed';
  const visible = (isActive || isTerminal) && !dismissed;

  useEffect(() => {
    if (prevStatusRef.current === 'idle' && status !== 'idle') {
      setDismissed(false);
      setExpanded(false);
    }
    prevStatusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }

    if (isTerminal && expanded) {
      collapseTimerRef.current = setTimeout(() => {
        setExpanded(false);
      }, COLLAPSE_AFTER_MS);
    }

    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
      }
    };
  }, [isTerminal, expanded]);

  if (!visible) return null;

  return (
    <div
      className={clsx(
        'fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-3 sm:right-6 z-30 max-w-[calc(100vw-1.5rem)]',
        'transition-all duration-300 ease-out',
        expanded
          ? 'w-[380px] max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain'
          : 'w-auto'
      )}
    >
      {expanded ? (
        <div
          className={clsx(
            'rounded-xl shadow-xl border',
            'bg-white dark:bg-slate-800',
            'border-gray-200 dark:border-slate-700',
            'animate-in fade-in slide-in-from-bottom-2'
          )}
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              Pricing Workflow
            </span>
            <div className="flex items-center gap-1">
              <button
                aria-label="Collapse pricing status"
                onClick={() => setExpanded(false)}
                className="rounded-md min-h-11 min-w-11 flex items-center justify-center p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <ChevronUp className="h-4 w-4 rotate-180" />
              </button>
              <button
                aria-label="Dismiss pricing status"
                onClick={() => setDismissed(true)}
                className="rounded-md min-h-11 min-w-11 flex items-center justify-center p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="px-2 pb-2">
            <PricingWorkflowStatus
              status={status}
              isExecuting={isExecuting}
              lastResult={lastResult}
              error={error}
              compact={false}
              showDetails={true}
            />
          </div>
        </div>
      ) : (
        <button
          aria-label="Expand pricing status"
          aria-expanded={false}
          onClick={() => setExpanded(true)}
          className={clsx(
            'flex items-center gap-2 rounded-full px-4 py-2.5 shadow-lg',
            'border transition-all duration-200',
            'hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]',
            status === 'completed' &&
              'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
            status === 'failed' &&
              'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
            isActive &&
              'bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-800'
          )}
        >
          <PricingWorkflowStatus
            status={status}
            isExecuting={isExecuting}
            lastResult={lastResult}
            error={error}
            compact={true}
            showDetails={false}
          />
        </button>
      )}
    </div>
  );
};

export default FloatingWorkflowStatus;
